<template>
  <div>
    <v-overlay color="#fff" :value="loading" :absolute="true">
      <Cog v-if="loading" />
    </v-overlay>
    <div :id="id"></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
// import * as am4plugins_regression from '@amcharts/amcharts4/plugins/regression';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import Cog from '@/components/animated/cog.vue';

am4core.addLicense('CH315878972');
/* Chart code */
am4core.options.queue = true;
// Themes begin
am4core.useTheme(am4themesAnimated);
// Themes end

export default {
  components: {
    Cog,
  },
  data() {
    return {
      chart: null,
      seriesRef: null,
      loading: true,
    };
  },
  props: {
    disposeChart: {
      type: Boolean,
    },
    id: {
      type: String,
      default: 'Bar Chart',
    },
    title: {
      type: String,
      default: 'Scatter Chart',
    },
    colors: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [
        {
          value: 90,
          date: new Date('2020-05-26T00:00:00'),
        },
        {
          value: 20,
          date: new Date('2020-05-27T00:00:00'),
        },
        {
          value: 30,
          date: new Date('2020-05-28T00:00:00'),
        },
        {
          value: 40,
          date: new Date('2020-05-29T00:00:00'),
        },
        {
          value: 55,
          date: new Date('2020-06-01T00:00:00'),
        },
        {
          value: 67,
          date: new Date('2020-06-02T00:00:00'),
        },
      ],
    },
    yAxis: {
      type: Object,
      default: () => ({ label: 'Annual_Average_Daily_Traffic' }),
    },
    xAxis: {
      type: Object,
      default: () => ({ label: 'Site' }),
    },
    height: {
      type: String,
      default: () => '400px',
    },
    width: {
      type: String,
      default: () => '100%',
    },
    labels: {
      type: Function,
      default: () => false,
    },
  },
  mounted() {
    const chartContainer = document.getElementById(this.id);
    chartContainer.style.width = this.width;
    chartContainer.style.height = this.height;
    chartContainer.style.paddingBottom = '15px';
    chartContainer.style.paddingRight = '5px';
    chartContainer.style.paddingLeft = '5px';

    this.chart = am4core.create(this.id, am4charts.XYChart);
    // Add chart title
    const title = this.chart.titles.create();
    title.text = this.title;
    title.fontSize = 25;
    title.marginBottom = 30;
    this.chart.data = this.data;
    this.chart.preloader.disabled = true;

    this.chart.events.on('ready', () => {
      this.loading = false;
    });
    this.chart.events.on('datavalidated', () => {
      this.loading = false;
    });
    this.chart.events.on('beforedatavalidated', () => {
      this.loading = true;
    });
    // Create axes
    const dateAxisX = this.chart.xAxes.push(new am4charts.DateAxis());
    // dateAxisX.groupData = true;
    dateAxisX.title.text = 'ISODatetime';
    dateAxisX.renderer.minGridDistance = 60;
    dateAxisX.baseInterval = {
      timeUnit: 'day',
      count: 1,
    };

    dateAxisX.renderer.labels.fontSize = 6;

    // Create value axis
    const valueAxisY = this.chart.yAxes.push(new am4charts.ValueAxis());
    valueAxisY.title.text = 'Cycle Count';

    // Create series
    const lineSeries = this.chart.series.push(new am4charts.LineSeries());
    lineSeries.name = 'Cycle Counters';
    lineSeries.dataFields.dateX = 'date';
    lineSeries.dataFields.valueY = 'value';
    lineSeries.tensionX = 0.77;
    lineSeries.strokeOpacity = 0;
    lineSeries.tooltipText = '{dateX}: {valueY}';

    // Add a bullet
    const bullet = lineSeries.bullets.push(new am4core.Circle());
    bullet.radius = 5;
    bullet.fill = this.chart.colors.getIndex(0);
    // Add trend line
    // const regseries = this.chart.series.push(new am4charts.LineSeries());
    // regseries.dataFields.dateX = 'date';
    // regseries.dataFields.valueY = 'value';
    // regseries.strokeWidth = 2;
    // regseries.name = 'Linear Regression';
    // const regPlugin = regseries.plugins.push(new am4plugins_regression.Regression());
    // regPlugin.simplify = true;
    // regPlugin.method = 'polynomial';
    // regPlugin.reorder = true;

    // Add cursor
    this.chart.cursor = new am4charts.XYCursor();
    this.chart.cursor.fullWidthLineX = true;
    this.chart.cursor.xAxis = dateAxisX;
    this.chart.cursor.lineX.strokeWidth = 0;
    this.chart.cursor.lineX.fill = am4core.color('#000');
    this.chart.cursor.lineX.fillOpacity = 0.1;
    // scrollbars
    this.chart.scrollbarX = new am4core.Scrollbar();
    this.chart.scrollbarY = new am4core.Scrollbar();

    let indicator;
    function showIndicator(chart) {
      if (indicator) {
        indicator.show();
      } else {
        indicator = chart.tooltipContainer.createChild(am4core.Container);
        indicator.background.fill = am4core.color('#fff');
        indicator.background.fillOpacity = 0.8;
        indicator.width = am4core.percent(100);
        indicator.height = am4core.percent(100);

        const indicatorLabel = indicator.createChild(am4core.Label);
        indicatorLabel.text = `This location has no data for the last 2 years.
Please take a look at the full dataset to see the historical data.`;
        indicatorLabel.align = 'center';
        indicatorLabel.valign = 'middle';
        indicatorLabel.fontSize = 20;
      }
    }
    function hideIndicator() {
      indicator.hide();
    }
    this.chart.events.on('beforevalidated', (ev) => {
      // check if there's data
      if (ev.target.data.length === 0) {
        showIndicator(this.chart);
      } else if (indicator) {
        hideIndicator();
      }
    });
  },
  watch: {
    data(newValue) {
      if (newValue) {
        this.chart.data = newValue;
      }
    },
    title(newValue) {
      if (newValue) {
        this.chart.titles.getIndex(0).text = newValue;
      }
    },
    disposeChart(newValue) {
      if (newValue) {
        this.chart.dispose();
      }
    },
  },
};
</script>

<style>
</style>
